import React, { useState, useEffect, Fragment } from "react";
import { Pagination, CircularProgress, Box } from "@mui/material";
import { IntlProvider } from "react-intl";
import givingFormLocales from "shared/locales/givingForm";
import { fetchLocale } from "shared/helpers/localeHelper";
import Header from "./Header/Header";
import Cards from "./CardList/Cards";
import MatchingGiftDescriptionModal from "./Modals/MatchingGiftDescriptionModal";
import "./MatchingGifts.scss";
import axiosInstanceMicrosite from "shared/api/axiosInstanceMicrosite";

const MatchingGifts = ({
  entityId,
  entityName,
  language,
  currencyCode,
  sectionData,
  featuresEnabled = [],
}) => {
  const [giftSectionId] = useState(sectionData.id);
  const [cardData, setCardData] = useState([]);
  const [sort, setSort] = useState("default");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = async (currentPage, currentSort, currentSearch) => {
    setIsLoading(true);

    try {
      const response = await axiosInstanceMicrosite.get(
        `/sections/matching_gifts_sections/${giftSectionId}`,
        {
          params: {
            entity_id: entityId,
            page: currentPage,
            sort: currentSort,
            search: currentSearch || "",
          },
        },
      );

      const data = response.data;

      // Handle the response structure based on your API
      if (data.matching_gifts_section?.matching_gift?.matching_gifts) {
        setCardData(data.matching_gifts_section.matching_gift.matching_gifts);
        setTotalPages(data.matching_gifts_section.matching_gift.total_pages || 0);
      } else if (data.matching_gifts) {
        setCardData(data.matching_gifts);
        setTotalPages(data.total_pages || 0);
      }
    } catch (error) {
      console.error("Error fetching matching gifts:", error);
      setCardData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFetchData = (sectionId, entityId, newPage, newSort, newSearch) => {
    setPage(newPage);
    setSort(newSort);
    setSearchTerm(newSearch);
    fetchData(newPage, newSort, newSearch);
  };

  const handlePageChange = (_event, newPage) => {
    setPage(newPage);
    fetchData(newPage, sort, searchTerm);
  };

  // Initial fetch
  useEffect(() => {
    fetchData(page, sort, searchTerm);
  }, []); // Only run once on mount

  return (
    <Fragment>
      <IntlProvider locale={fetchLocale(language)} messages={givingFormLocales["en"]}>
        <MatchingGiftDescriptionModal
          sectionId={giftSectionId}
          currencyCode={currencyCode}
          modalName="Matching Gift Description"
        />
        <div className="matching-gifts">
          <h2 id={entityId} className="matching-gifts-title">
            {sectionData.name}
          </h2>
          <div className="matching-gifts-content">
            <Header
              entityId={entityId}
              entityName={entityName}
              sectionId={giftSectionId}
              selectId={`select-${entityId}`}
              searchTerm={searchTerm}
              sort={sort}
              featuresEnabled={featuresEnabled}
              fetchData={handleFetchData}
            />
          </div>

          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="300px"
              role="status"
              aria-live="polite"
              aria-label="Loading matching gifts">
              <CircularProgress size={60} />
            </Box>
          ) : cardData.length === 0 ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
              fontSize="1.2rem">
              No matching gifts found
            </Box>
          ) : (
            <Cards
              sectionId={giftSectionId}
              entityId={entityId}
              currencyCode={currencyCode}
              data={cardData}
            />
          )}
        </div>

        {!isLoading && totalPages > 0 && (
          <>
            <div
              className="hacked-pages"
              style={{ display: "flex", justifyContent: "center" }}>
              <Pagination
                count={totalPages}
                page={page}
                showFirstButton={true}
                showLastButton={true}
                siblingCount={0}
                boundaryCount={1}
                onChange={handlePageChange}
                disabled={isLoading}
                aria-label={`${sectionData.name} pagination`}
              />
            </div>
            <div className="hacked-page-numbers">
              {page} / {totalPages}
            </div>
          </>
        )}
      </IntlProvider>
    </Fragment>
  );
};

export default MatchingGifts;
